import { useTranslation } from 'react-i18next';
import { Menu } from '@components/ui/Menu/Menu';
import type { INotificationItem } from '@interfaces/INotificationItem';
import { CloseIcon } from '@utils/icons';
import { formatRecentTime } from '@utils/formatRecentTime';
import styles from './NotificationRow.module.css';

/** What a row shows of an item: its visual — dotted while unread — what is waiting, and when. */
export function NotificationItemBody({ item }: { item: INotificationItem }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <span className={styles.visual}>
        {item.visual}
        {item.unread && <span className={styles.unreadDot} aria-label={t('notifications.unread')} />}
      </span>
      <span className={styles.content}>
        <span className={styles.text}>{item.text}</span>
        {item.detail && <span className={styles.detail}>{item.detail}</span>}
      </span>
      <span className={styles.time}>{formatRecentTime(item.at, i18n.language, t)}</span>
    </>
  );
}

export interface INotificationRowProps {
  item: INotificationItem;
  onOpen: () => void;
}

/** One row of the bell's menu, drawing one item — with its × when it can be dismissed. */
export function NotificationRow({ item, onOpen }: INotificationRowProps) {
  const { t } = useTranslation();
  const { onDismiss } = item;
  return (
    <Menu.Item className={styles.row} onClick={onOpen}>
      <NotificationItemBody item={item} />
      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={t('notifications.dismiss')}
          onClick={(e) => {
            // The row behind it opens the item; the × only takes it away.
            e.stopPropagation();
            onDismiss();
          }}
        >
          <CloseIcon size={12} />
        </button>
      )}
    </Menu.Item>
  );
}
